import { useState } from 'react';
import { DollarSign, CreditCard, Loader2, ExternalLink, Copy, Check, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const PLANES = [
  { key: 'starter', name: 'Starter', price: 697 },
  { key: 'duo', name: 'Duo', price: 997 },
  { key: 'pro', name: 'Profesional', price: 1497 },
  { key: 'empire', name: 'Empire', price: 2495 },
  { key: 'club_vacacional', name: 'Club Vacacional', price: 1997 },
];

const TIPOS = [
  { key: 'full', label: 'Pago Completo', color: 'bg-emerald-500' },
  { key: '4_cuotas', label: '4 Cuotas (+15%)', color: 'bg-blue-500' },
  { key: '8_cuotas', label: '8 Cuotas (+25%)', color: 'bg-violet-500' },
  { key: '12_cuotas', label: '12 Cuotas (+35%)', color: 'bg-amber-500' },
];

function calcAmount(price, type) {
  if (type === 'full') return { inicial: price, cuota: null, num: null };
  const cfg = { '4_cuotas': [4, 0.15], '8_cuotas': [8, 0.25], '12_cuotas': [12, 0.35] };
  const [n, s] = cfg[type];
  const inicial = +(price * 0.25).toFixed(2);
  const cuota = +((price * 0.75 * (1 + s)) / n).toFixed(2);
  return { inicial, cuota, num: n };
}

export default function PaymentLinksPage() {
  const [links, setLinks] = useState({});
  const [loading, setLoading] = useState({});
  const [copied, setCopied] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [openAmount, setOpenAmount] = useState('');
  const [openDesc, setOpenDesc] = useState('');
  const [openEmail, setOpenEmail] = useState('');
  const [openLoading, setOpenLoading] = useState(false);
  const [openLink, setOpenLink] = useState(null);
  const [memAmount, setMemAmount] = useState('');
  const [memName, setMemName] = useState('');
  const [memEmail, setMemEmail] = useState('');
  const [memInterval, setMemInterval] = useState('month');
  const [memLoading, setMemLoading] = useState(false);
  const [memLink, setMemLink] = useState(null);

  const generateLink = async (plan, type) => {
    const k = `${plan}_${type}`;
    setLoading(p => ({ ...p, [k]: true }));

    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        if (attempt > 0) toast.info(`Reintentando ${plan} ${type}... (${attempt + 1}/5)`);
        const ts = Date.now();
        const leadRes = await fetch(`${API_URL}/api/leads`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nombre: 'Link', apellido: 'Directo',
            email: `link_${plan}_${type}_${ts}@clubviajemos.com`,
            pais: 'Internacional', plan_seleccionado: plan,
            secretos_elegidos: ['s1']
          })
        });
        const lead = await leadRes.json();
        if (!lead.id) throw new Error('No lead ID');

        const checkoutRes = await fetch(`${API_URL}/api/checkout/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lead_id: lead.id,
            origin_url: window.location.origin,
            payment_type: type
          })
        });
        const checkout = await checkoutRes.json();
        if (checkout.checkout_url) {
          setLinks(p => ({ ...p, [k]: { url: checkout.checkout_url, time: new Date().toLocaleTimeString() } }));
          toast.success('Link generado');
          setLoading(p => ({ ...p, [k]: false }));
          return;
        }
        throw new Error(checkout.detail || 'No checkout URL');
      } catch (err) {
        if (attempt < 4) await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
      }
    }
    toast.error(`Error generando link ${plan} ${type}. Intenta de nuevo.`);
    setLoading(p => ({ ...p, [k]: false }));
  };

  const generateAll = async (planKey) => {
    for (const tipo of TIPOS) {
      const k = `${planKey}_${tipo.key}`;
      if (links[k]) continue; // Skip already generated
      await generateLink(planKey, tipo.key);
      await new Promise(r => setTimeout(r, 500)); // Wait between each
    }
  };

  const copyLink = (k, url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(k);
      toast.success('Link copiado');
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const togglePlan = (planKey) => {
    setExpanded(p => ({ ...p, [planKey]: !p[planKey] }));
  };

  const generateOpenLink = async () => {
    const amt = parseFloat(openAmount);
    if (!amt || amt < 1) { toast.error('Monto minimo: $1 USD'); return; }
    if (amt > 50000) { toast.error('Monto maximo: $50,000 USD'); return; }

    setOpenLoading(true);
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        if (attempt > 0) toast.info(`Reintentando... (${attempt + 1}/5)`);
        const res = await fetch(`${API_URL}/api/checkout/open`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: amt,
            description: openDesc || `Pago Club Viajemos - $${amt} USD`,
            email: openEmail || undefined,
            origin_url: window.location.origin
          })
        });
        const data = await res.json();
        if (data.checkout_url) {
          setOpenLink({ url: data.checkout_url, time: new Date().toLocaleTimeString(), amount: amt });
          toast.success('Link generado');
          setOpenLoading(false);
          return;
        }
        if (res.status >= 400 && res.status < 500) {
          toast.error(data.detail || 'Error');
          break;
        }
      } catch (err) { console.error(`Open link attempt ${attempt + 1} failed:`, err); }
      if (attempt < 4) await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
    }
    if (!openLink) toast.error('Error generando link. Intenta de nuevo.');
    setOpenLoading(false);
  };

  const generateMembershipLink = async () => {
    const amt = parseFloat(memAmount);
    if (!amt || amt < 1) { toast.error('Monto minimo: $1 USD'); return; }

    setMemLoading(true);
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        if (attempt > 0) toast.info(`Reintentando membresia... (${attempt + 1}/5)`);
        const res = await fetch(`${API_URL}/api/checkout/membership`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: amt,
            interval: memInterval,
            name: memName || `Membresia Club Viajemos - $${amt}/${memInterval === 'month' ? 'mes' : 'ano'}`,
            email: memEmail || undefined,
            origin_url: window.location.origin
          })
        });
        const data = await res.json();
        if (data.checkout_url) {
          setMemLink({ url: data.checkout_url, time: new Date().toLocaleTimeString(), amount: amt, interval: memInterval });
          toast.success('Link de membresia generado');
          setMemLoading(false);
          return;
        }
        if (res.status >= 400 && res.status < 500) {
          toast.error(data.detail || 'Error');
          break;
        }
      } catch (err) { console.error(`Membership link attempt ${attempt + 1} failed:`, err); }
      if (attempt < 4) await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
    }
    if (!memLink) toast.error('Error generando link. Intenta de nuevo.');
    setMemLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <header className="border-b border-slate-800 bg-[#0d1321]">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-[#1A1A2E]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Generador de Links de Pago</h1>
              <p className="text-xs text-slate-500">Club Viajemos / Travel Empire</p>
            </div>
          </div>
          <a href="/" className="text-xs text-slate-500 hover:text-white transition-colors">Volver al inicio</a>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Info */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-8">
          <p className="text-amber-300 text-sm font-medium">Los links de Stripe expiran en 24 horas. Genera links frescos antes de enviarlos a tus clientes.</p>
        </div>

        {/* Plans */}
        <div className="space-y-4">
          {PLANES.map(plan => (
            <div key={plan.key} className="bg-[#151B2B] border border-slate-700/50 rounded-xl overflow-hidden">
              {/* Plan Header */}
              <button
                onClick={() => togglePlan(plan.key)}
                data-testid={`plan-toggle-${plan.key}`}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#45A0EE] to-[#80C753] flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h2 className="text-base font-bold text-white">{plan.name}</h2>
                    <p className="text-xs text-slate-400">${plan.price.toLocaleString()} USD</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); generateAll(plan.key); }}
                    data-testid={`generate-all-${plan.key}`}
                    className="text-xs bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 px-3 py-1.5 rounded-lg transition-colors hidden sm:block"
                  >
                    Generar todos
                  </button>
                  {expanded[plan.key] ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                </div>
              </button>

              {/* Payment Types */}
              {expanded[plan.key] && (
                <div className="border-t border-slate-700/50 divide-y divide-slate-700/30">
                  {TIPOS.map(tipo => {
                    const k = `${plan.key}_${tipo.key}`;
                    const info = calcAmount(plan.price, tipo.key);
                    const link = links[k];
                    const isLoading = loading[k];

                    return (
                      <div key={tipo.key} className="px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
                        {/* Type info */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className={`w-2 h-8 rounded-full ${tipo.color} flex-shrink-0`} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white">{tipo.label}</p>
                            <p className="text-[11px] text-slate-500">
                              {info.cuota
                                ? `Hoy: $${info.inicial} + ${info.num}x $${info.cuota}/mes`
                                : `$${plan.price.toLocaleString()} USD`}
                            </p>
                          </div>
                        </div>

                        {/* Link / Generate */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {link ? (
                            <>
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`link-open-${k}`}
                                className="flex items-center gap-1.5 text-xs bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 px-3 py-2 rounded-lg transition-colors"
                              >
                                <ExternalLink className="h-3.5 w-3.5" /> Abrir
                              </a>
                              <button
                                onClick={() => copyLink(k, link.url)}
                                data-testid={`link-copy-${k}`}
                                className="flex items-center gap-1.5 text-xs bg-slate-700 text-slate-300 hover:bg-slate-600 px-3 py-2 rounded-lg transition-colors"
                              >
                                {copied === k ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                                {copied === k ? 'Copiado' : 'Copiar'}
                              </button>
                              <span className="text-[10px] text-slate-600 hidden sm:block">{link.time}</span>
                            </>
                          ) : (
                            <button
                              onClick={() => generateLink(plan.key, tipo.key)}
                              disabled={isLoading}
                              data-testid={`generate-${k}`}
                              className="flex items-center gap-1.5 text-xs bg-[#45A0EE]/20 text-[#45A0EE] hover:bg-[#45A0EE]/30 disabled:opacity-50 px-4 py-2 rounded-lg transition-colors"
                            >
                              {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
                              {isLoading ? 'Generando...' : 'Generar Link'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Open Amount Section */}
        <div className="mt-8 bg-gradient-to-br from-[#151B2B] to-[#1a2235] border border-amber-500/30 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-amber-500/20 bg-amber-500/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-[#1A1A2E]" />
              </div>
              <div>
                <h2 className="text-base font-bold text-amber-300">Monto Abierto</h2>
                <p className="text-xs text-slate-400">Genera un link de pago con el monto que necesites</p>
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 block">Monto USD *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400 font-bold text-sm">$</span>
                  <input
                    type="number"
                    min="1"
                    max="50000"
                    step="0.01"
                    placeholder="500.00"
                    value={openAmount}
                    onChange={e => setOpenAmount(e.target.value)}
                    data-testid="open-amount-input"
                    className="w-full pl-7 pr-3 py-2.5 rounded-lg bg-white/5 border border-slate-700 text-white text-sm focus:outline-none focus:border-amber-400"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 block">Descripcion (opcional)</label>
                <input
                  type="text"
                  placeholder="Ej: Pago Secreto #1"
                  value={openDesc}
                  onChange={e => setOpenDesc(e.target.value)}
                  data-testid="open-desc-input"
                  className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-slate-700 text-white text-sm focus:outline-none focus:border-amber-400 placeholder:text-slate-600"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 block">Email cliente (opcional)</label>
                <input
                  type="email"
                  placeholder="cliente@email.com"
                  value={openEmail}
                  onChange={e => setOpenEmail(e.target.value)}
                  data-testid="open-email-input"
                  className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-slate-700 text-white text-sm focus:outline-none focus:border-amber-400 placeholder:text-slate-600"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <button
                onClick={generateOpenLink}
                disabled={openLoading}
                data-testid="generate-open-link"
                className="flex items-center gap-2 text-sm font-bold px-6 py-2.5 rounded-xl text-[#1A1A2E] disabled:opacity-50 transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #D4AF37, #F0D060, #D4AF37)' }}
              >
                {openLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                {openLoading ? 'Generando...' : 'Generar Link de Pago'}
              </button>

              {openLink && (
                <div className="flex items-center gap-2">
                  <a
                    href={openLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-link-open"
                    className="flex items-center gap-1.5 text-xs bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 px-3 py-2 rounded-lg transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> Abrir (${openLink.amount})
                  </a>
                  <button
                    onClick={() => copyLink('open', openLink.url)}
                    data-testid="open-link-copy"
                    className="flex items-center gap-1.5 text-xs bg-slate-700 text-slate-300 hover:bg-slate-600 px-3 py-2 rounded-lg transition-colors"
                  >
                    {copied === 'open' ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied === 'open' ? 'Copiado' : 'Copiar'}
                  </button>
                  <span className="text-[10px] text-slate-600">{openLink.time}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Membership Section */}
        <div className="mt-4 bg-gradient-to-br from-[#151B2B] to-[#1a2235] border border-violet-500/30 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-violet-500/20 bg-violet-500/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center">
                <RefreshCw className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-violet-300">Membresia Recurrente</h2>
                <p className="text-xs text-slate-400">Crea un link de suscripcion con cobro automatico mensual o anual</p>
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 block">Monto USD *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-400 font-bold text-sm">$</span>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="49.99"
                    value={memAmount}
                    onChange={e => setMemAmount(e.target.value)}
                    data-testid="mem-amount-input"
                    className="w-full pl-7 pr-3 py-2.5 rounded-lg bg-white/5 border border-slate-700 text-white text-sm focus:outline-none focus:border-violet-400"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 block">Frecuencia *</label>
                <select
                  value={memInterval}
                  onChange={e => setMemInterval(e.target.value)}
                  data-testid="mem-interval-select"
                  className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-slate-700 text-white text-sm focus:outline-none focus:border-violet-400"
                >
                  <option value="month" className="bg-[#151B2B]">Mensual</option>
                  <option value="year" className="bg-[#151B2B]">Anual</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 block">Nombre (opcional)</label>
                <input
                  type="text"
                  placeholder="Ej: VIP Mensual"
                  value={memName}
                  onChange={e => setMemName(e.target.value)}
                  data-testid="mem-name-input"
                  className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-slate-700 text-white text-sm focus:outline-none focus:border-violet-400 placeholder:text-slate-600"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 block">Email cliente (opcional)</label>
                <input
                  type="email"
                  placeholder="cliente@email.com"
                  value={memEmail}
                  onChange={e => setMemEmail(e.target.value)}
                  data-testid="mem-email-input"
                  className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-slate-700 text-white text-sm focus:outline-none focus:border-violet-400 placeholder:text-slate-600"
                />
              </div>
            </div>

            {memAmount && parseFloat(memAmount) > 0 && (
              <p className="text-xs text-slate-500 mb-3">
                Se cobrara <span className="text-violet-300 font-medium">${parseFloat(memAmount).toFixed(2)} USD</span> {memInterval === 'month' ? 'cada mes' : 'cada ano'} de forma automatica.
              </p>
            )}

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <button
                onClick={generateMembershipLink}
                disabled={memLoading}
                data-testid="generate-mem-link"
                className="flex items-center gap-2 text-sm font-bold px-6 py-2.5 rounded-xl text-white disabled:opacity-50 transition-all hover:scale-105 bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700"
              >
                {memLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {memLoading ? 'Generando...' : 'Generar Link de Membresia'}
              </button>

              {memLink && (
                <div className="flex items-center gap-2">
                  <a
                    href={memLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mem-link-open"
                    className="flex items-center gap-1.5 text-xs bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 px-3 py-2 rounded-lg transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> Abrir (${memLink.amount}/{memLink.interval === 'month' ? 'mes' : 'ano'})
                  </a>
                  <button
                    onClick={() => copyLink('mem', memLink.url)}
                    data-testid="mem-link-copy"
                    className="flex items-center gap-1.5 text-xs bg-slate-700 text-slate-300 hover:bg-slate-600 px-3 py-2 rounded-lg transition-colors"
                  >
                    {copied === 'mem' ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied === 'mem' ? 'Copiado' : 'Copiar'}
                  </button>
                  <span className="text-[10px] text-slate-600">{memLink.time}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="text-center text-[10px] text-slate-600 mt-8">Links generados via Stripe Checkout. Expiran en 24 horas. Uso exclusivo para agentes autorizados.</p>
      </div>
    </div>
  );
}
